import { CSSProperties, useCallback } from "react";
import { ToastEnum, ToastStatusEnum, useToast } from "../../hooks/toast";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import ConfirmationModal from "../shared/ConfirmationModal";
import { BuilderStatus } from "../../utils/builders";

export const STATUS_COLORS: Record<BuilderStatus, string> = {
  ACTIVE: COLORS.green.one,
  SUSPENDED: COLORS.orange.one,
  DISABLED: COLORS.red.one,
};

export const panelStyle: CSSProperties = {
  background: BACKGROUND_COLORS.componentSolid,
  border: `1px solid ${BORDER_COLORS.one}`,
  borderRadius: 8,
  padding: SPACING.three,
  marginBottom: SPACING.three,
};

export const inputStyle: CSSProperties = {
  width: "100%",
  marginTop: SPACING.one,
  height: 32,
  padding: `0 ${SPACING.two}px`,
  borderRadius: 4,
  border: `1px solid ${BORDER_COLORS.one}`,
  background: BACKGROUND_COLORS.component,
  color: TEXT_COLORS.one,
  boxSizing: "border-box",
};

export const labelStyle: CSSProperties = {
  ...FONT_STYLE.label.two,
  color: TEXT_COLORS.two,
  display: "block",
};

export const errorStyle: CSSProperties = {
  ...FONT_STYLE.label.three,
  color: COLORS.red.one,
  marginTop: SPACING.one,
};

export const cellStyle: CSSProperties = {
  padding: `${SPACING.two}px ${SPACING.three}px`,
  borderBottom: `1px solid ${BORDER_COLORS.two}`,
  color: TEXT_COLORS.one,
  textAlign: "left",
  whiteSpace: "nowrap",
};

export const headerCellStyle: CSSProperties = {
  ...cellStyle,
  ...FONT_STYLE.label.two,
  color: TEXT_COLORS.two,
};

export function StatusBadge({ status }: { status: BuilderStatus }) {
  const color = STATUS_COLORS[status] ?? TEXT_COLORS.two;
  return (
    <span
      style={{
        ...FONT_STYLE.label.three,
        color,
        border: `1px solid ${color}`,
        borderRadius: 4,
        padding: `2px ${SPACING.one}px`,
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}

export function CopyText({ text }: { text: string }) {
  const { addToast } = useToast();
  const onCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    addToast(
      {
        type: ToastEnum.SIMPLE,
        header: "Copied",
        subheader: text,
        status: ToastStatusEnum.SUCCESS,
      },
      2000
    );
  }, [addToast, text]);
  return (
    <button
      type="button"
      onClick={onCopy}
      title="Copy"
      style={{
        background: "transparent",
        border: "none",
        color: COLORS.blue.one,
        cursor: "pointer",
        fontFamily: "monospace",
        padding: 0,
      }}
    >
      {text}
    </button>
  );
}

interface IStatusConfirmModalsProps {
  show: boolean;
  // The status being confirmed; undefined hides every confirmation.
  target?: BuilderStatus;
  builderName: string;
  submitting: boolean;
  onHide: () => void;
  onConfirm: () => void;
}

/**
 * Confirmations for status changes shared by the edit form and the detail
 * view: SUSPENDED and DISABLED always confirm; ACTIVE confirms a reactivation.
 */
export function StatusConfirmModals({
  show, target, builderName, submitting, onHide, onConfirm,
}: IStatusConfirmModalsProps) {
  return (
    <>
      <ConfirmationModal
        show={show && target === "SUSPENDED"}
        onHide={onHide}
        title="Suspend builder?"
        primaryColor={COLORS.orange.one}
        width="420px"
        minWidth="420px"
        isLoading={submitting}
        stats={[
          { title: "Builder", data: builderName },
          { title: "New status", data: "SUSPENDED" },
        ]}
        warning="New builder orders will be rejected; resting orders fill with zero builder fee."
        confirmationButton={{ title: "Suspend", onClick: onConfirm }}
      />
      <ConfirmationModal
        show={show && target === "DISABLED"}
        onHide={onHide}
        title="Disable builder?"
        primaryColor={COLORS.red.one}
        width="420px"
        minWidth="420px"
        isLoading={submitting}
        stats={[
          { title: "Builder", data: builderName },
          { title: "New status", data: "DISABLED" },
        ]}
        warning="Disabling stops new builder orders and builder fees; it can be undone by reactivating."
        confirmationButton={{ title: "Disable", onClick: onConfirm }}
      />
      <ConfirmationModal
        show={show && target === "ACTIVE"}
        onHide={onHide}
        title="Reactivate builder?"
        primaryColor={COLORS.green.one}
        width="420px"
        minWidth="420px"
        isLoading={submitting}
        stats={[
          { title: "Builder", data: builderName },
          { title: "New status", data: "ACTIVE" },
        ]}
        warning="Builder orders and approvals will be accepted again."
        confirmationButton={{ title: "Reactivate", onClick: onConfirm }}
      />
    </>
  );
}
